"use client";

import moment from "moment";
import { Button } from "./ui/button";
import { EmployeePerformanceCard } from "./employee-performance-card";
import { EmployeeTipBreakdown } from "./employee-tip-breakdown";
import {
  X,
  User,
  Loader2,
  DollarSign,
  Calendar,
  Clock,
  Sun,
  Moon,
  TrendingUp,
} from "lucide-react";

const money = (value) => {
  const numeric = parseFloat(value);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : "0.00";
};

export default function EmployeeDetailsModal({
  isOpen,
  onClose,
  loading = false,
  error = null,
  employeeDetails = null,
  dateRange = null,
  fallbackName = "",
}) {
  if (!isOpen) return null;

  const employee = employeeDetails?.employee;
  const summary = employeeDetails?.completeData?.summary || {};
  const performance = employeeDetails?.performanceMetrics || {};
  const shift =
    employeeDetails?.completeData?.shift_breakdown ||
    performance?.shift_breakdown ||
    {};

  const workingHours =
    summary.working_hours ??
    performance?.performance_summary?.working_hours ??
    0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Name: {employee?.name || fallbackName}
                {employee?.role ? (
                  <span className="text-gray-500 font-normal"> ({employee.role})</span>
                ) : null}
                {" | "}
                Working Hrs:{" "}
                <span className="text-blue-700">{Number(workingHours || 0).toFixed(1)} hrs</span>
              </h2>
              <p className="text-sm text-gray-500">
                Employee tip & payroll details
                {dateRange?.start && dateRange?.end && (
                  <span className="ml-2">
                    ({moment(dateRange.start).format("MM/DD/YYYY")} -{" "}
                    {moment(dateRange.end).format("MM/DD/YYYY")})
                  </span>
                )}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">{error}</div>
          ) : employeeDetails ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                <EmployeePerformanceCard
                  title="Total Tips"
                  value={money(summary.total_tips)}
                  icon={<DollarSign className="h-4 w-4" />}
                  color="blue"
                />
                <EmployeePerformanceCard
                  title="Morning Tips (12 PM–5:59 PM CT)"
                  value={`$${money(shift.morning ?? summary.morning_tips)}`}
                  icon={<Sun className="h-4 w-4" />}
                  color="green"
                />
                <EmployeePerformanceCard
                  title="Evening Tips (6 PM–5:59 AM CT)"
                  value={`$${money(shift.evening ?? summary.evening_tips)}`}
                  icon={<Moon className="h-4 w-4" />}
                  color="purple"
                />
                <EmployeePerformanceCard
                  title="Working Hrs"
                  value={`${Number(workingHours || 0).toFixed(1)} hrs`}
                  icon={<Clock className="h-4 w-4" />}
                  color="blue"
                />
                <EmployeePerformanceCard
                  title="Working Days"
                  value={performance?.performance_summary?.working_days || 0}
                  icon={<Calendar className="h-4 w-4" />}
                  color="green"
                />
                <EmployeePerformanceCard
                  title="Avg Tips/Day"
                  value={money(performance?.performance_summary?.avg_tips_per_day)}
                  icon={<TrendingUp className="h-4 w-4" />}
                  color="purple"
                />
              </div>

              {(Number(shift.morning || 0) > 0 || Number(shift.evening || 0) > 0) && (
                <div className="mb-6 p-4 rounded-lg border bg-slate-50">
                  <h3 className="font-semibold text-gray-800 mb-2">Shift Comparison</h3>
                  <p className="text-xs text-gray-500 mb-2">America/Chicago. Morning is 12:00 PM–5:59 PM. Evening is 6:00 PM–5:59 AM.</p>
                  <p className="text-sm text-gray-600">
                    {Number(shift.evening || 0) > Number(shift.morning || 0)
                      ? "Evening shifts are currently more tip-encouraging for this employee."
                      : Number(shift.morning || 0) > Number(shift.evening || 0)
                        ? "Morning shifts are currently more tip-encouraging for this employee."
                        : "Morning and evening tip totals are currently similar."}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-lg border">
                  <div className="p-4 border-b bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-800">Tip Breakdown by Role</h3>
                  </div>
                  <EmployeeTipBreakdown
                    data={employeeDetails?.completeData?.tip_breakdown}
                    role={employee?.role}
                  />
                </div>

                <div className="bg-white rounded-lg border">
                  <div className="p-4 border-b bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-800">Performance Metrics</h3>
                  </div>
                  <div className="p-4 space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium">Best Day:</span>
                      <span>
                        {performance?.daily_performance?.best_day
                          ? `${performance.daily_performance.best_day.date}: $${money(
                              performance.daily_performance.best_day.amount
                            )}`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Worst Day:</span>
                      <span>
                        {performance?.daily_performance?.worst_day
                          ? `${performance.daily_performance.worst_day.date}: $${money(
                              performance.daily_performance.worst_day.amount
                            )}`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Paid Out:</span>
                      <span>${money(summary.paid_out)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Total Tax:</span>
                      <span>${money(summary.total_tax)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {employeeDetails?.completeData?.timeline?.all_transactions?.length > 0 && (
                <div className="bg-white rounded-lg border">
                  <div className="p-4 border-b bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-800">Recent Tip / Pay Activity</h3>
                  </div>
                  <div className="overflow-x-auto max-h-64">
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {employeeDetails.completeData.timeline.all_transactions
                          .slice(0, 15)
                          .map((tx, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm">
                                {moment(tx.date).format("MM/DD/YYYY")}
                              </td>
                              <td className="px-4 py-3 text-sm capitalize">
                                {(tx.type || "").replace(/_/g, " ")}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium">
                                ${money(tx.amount)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {tx.description || "-"}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">No employee details available.</div>
          )}
        </div>

        <div className="flex justify-end p-6 border-t bg-gray-50 sticky bottom-0">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
